// HtmlCharsets.jsx
import React from "react";

const HtmlCharsets = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML Character Sets (Charsets) - Comprehensive Notes</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction to HTML Charsets</h2>
        <p>
          A <strong>character set</strong> defines the encoding used to display text in a web page.
          It ensures that characters from different languages, symbols, and emojis render correctly.
          Most modern web pages use <code>UTF-8</code>, which supports almost all characters.
        </p>
      </section>

      {/* Declaring Charset in HTML */}
      <section id="declaring-charset">
        <h2>2. Declaring Charset in HTML</h2>
        <p>The charset is declared inside the <code>&lt;head&gt;</code> section:</p>
        <pre>
          <code>
{`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>Document with UTF-8 charset</title>
</head>
<body>
    <p>Hello, world! 👋</p>
</body>
</html>`}
          </code>
        </pre>
        <p>
          - <code>UTF-8</code> is the most widely used encoding.<br />
          - Older encodings include <code>ISO-8859-1</code> (Latin-1) and <code>Windows-1252</code>.
        </p>
      </section>

      {/* Common Charsets */}
      <section id="common-charsets">
        <h2>3. Common Charsets</h2>
        <ul>
          <li><strong>UTF-8</strong>: Universal character encoding, supports all languages and emojis.</li>
          <li><strong>ISO-8859-1</strong>: Western European languages.</li>
          <li><strong>Windows-1252</strong>: Similar to ISO-8859-1, used in Windows.</li>
          <li><strong>Shift_JIS</strong>: Japanese characters.</li>
          <li><strong>GB2312</strong>: Simplified Chinese characters.</li>
        </ul>
      </section>

      {/* Importance of Charset */}
      <section id="importance">
        <h2>4. Importance of Declaring Charset</h2>
        <ul>
          <li>Prevents garbled text when displaying special characters.</li>
          <li>Ensures proper rendering of emojis and non-English characters.</li>
          <li>Helps search engines and browsers correctly interpret content.</li>
        </ul>
      </section>

      {/* HTML Meta Charset Examples */}
      <section id="meta-examples">
        <h2>5. HTML Meta Charset Examples</h2>
        <p>UTF-8:</p>
        <pre>
          <code>{`<meta charset="UTF-8">`}</code>
        </pre>
        <p>ISO-8859-1:</p>
        <pre>
          <code>{`<meta charset="ISO-8859-1">`}</code>
        </pre>
        <p>Windows-1252:</p>
        <pre>
          <code>{`<meta charset="Windows-1252">`}</code>
        </pre>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>6. Best Practices</h2>
        <ul>
          <li>Always use <code>UTF-8</code> for new projects.</li>
          <li>Place the <code>&lt;meta charset&gt;</code> tag as the first element inside <code>&lt;head&gt;</code>.</li>
          <li>Test pages with multiple languages and symbols to ensure compatibility.</li>
          <li>Set server-side encoding headers to match the declared charset.</li>
        </ul>
      </section>

      {/* Summary */}
      <section id="summary">
        <h2>7. Summary</h2>
        <p>
          Correctly declaring the charset is crucial for proper text display, accessibility, and SEO.
          <strong>UTF-8</strong> is the preferred encoding for modern web development.
        </p>
      </section>
    </div>
  );
};

export default HtmlCharsets;
